import { api, getCsrf, getSessionToken, setCsrf, setSessionToken } from "../api";

export { getCsrf, setCsrf, getSessionToken, setSessionToken };

export type DeveloperRole = "superadmin" | "developer";
export type DeveloperStatus = "pending_setup" | "active" | "disabled";

export type SessionAuthMethod = "password" | "biometric";

export interface Developer {
  id: string;
  name: string;
  email: string;
  role: DeveloperRole;
  status: DeveloperStatus;
  company: string | null;
  permissions: string[];
  created_at?: string | null;
  updated_at?: string | null;
  last_login_at?: string | null;
  has_avatar?: boolean;
  avatar_url?: string | null;
}

export function developerAvatarSrc(me: Pick<Developer, "id" | "has_avatar" | "avatar_url" | "updated_at">): string | null {
  if (!me.has_avatar) return null;
  const path = me.avatar_url || "/api/developers/account/avatar";
  return `${path}?v=${encodeURIComponent(me.updated_at || me.id)}`;
}

export interface ApiKeyRow {
  id: string;
  name: string;
  environment: "live" | "test";
  prefix: string;
  masked: string;
  scopes: string[];
  rate_limit_rpm?: number;
  created_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  disabled_at?: string | null;
  status: "active" | "disabled" | "revoked";
}

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 200;

export function passwordRequirementText(): string {
  return `Password must be ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters.`;
}

export function validatePassword(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`;
  }
  return null;
}

export async function devApi<T>(path: string, init?: RequestInit): Promise<T> {
  return api<T>(path, init);
}

export async function fetchSession(): Promise<{
  developer: Developer;
  csrfToken: string;
  auth_method: SessionAuthMethod;
} | null> {
  const headers = new Headers();
  const session = getSessionToken();
  if (session) headers.set("Authorization", `Bearer ${session}`);
  const res = await fetch("/api/developers/session", { credentials: "include", headers, cache: "no-store" });
  if (res.status === 401) {
    setSessionToken("");
    return null;
  }
  if (!res.ok) return null;
  const body = (await res.json()) as {
    developer: Developer;
    csrfToken: string;
    auth_method?: SessionAuthMethod;
  };
  setCsrf(body.csrfToken);
  return {
    developer: body.developer,
    csrfToken: body.csrfToken,
    auth_method: body.auth_method === "biometric" ? "biometric" : "password",
  };
}
